<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import {
  Activity,
  BadgeCheck,
  Ban,
  BarChart3,
  Boxes,
  CheckCircle2,
  ChevronRight,
  CircleGauge,
  CloudCog,
  Database,
  FileClock,
  KeyRound,
  Laptop,
  Languages,
  LockKeyhole,
  LogOut,
  Menu,
  Moon,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  ServerCog,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  UserCog,
  Users,
  X
} from 'lucide-vue-next';
import {
  ADMIN_PERMISSIONS,
  DEEPL_MODEL_TYPES,
  type AdminAccount,
  type AdminAccountStatus,
  AdminAuditLog,
  AdminClientRelease,
  AdminDevice,
  AdminLicense,
  type AdminLoginResult,
  AdminOverview,
  type AdminPermission,
  type RegistrationDefaults,
  AdminUserUsageSummary,
  type AdminTranslationUsageSummary,
  AdminUser,
  ClientUpdateType,
  LicenseStatus,
  ReleaseChannel,
  TranslationProvider,
  TranslationPublicConfig,
  UserStatus
} from '@sky-lingo-v2/shared';
import {
  ApiError,
  api,
  getAdminToken,
  setAdminToken
} from './api';
import { buildTranslationUpdate, createProviderInputs, credentialHint, defaultRegistrationProvider, primaryProviderIds, providerDetails, providerIds } from './translationProviders';
import TranslationUsagePanel from './TranslationUsagePanel.vue';
import TranslationSettingsPanel from './TranslationSettingsPanel.vue';
import ProviderLogo from './ProviderLogo.vue';
import UserLicensePanel from './UserLicensePanel.vue';
import { canOpenUserManagement, readUserAccess, saveUserAccess, type AccessUpdate } from './userLicenses';
import PaginationBar from './PaginationBar.vue';
import { countDeviceStates, filterDevicesByState, filterUsersByUsername, paginateAdminRows, type DeviceStateFilter, type UsernameMatchMode } from './adminLists';
import { readAllAdminRows } from './readAdminRows';
import { readUsageView, type UsagePeriodKey } from './translationUsage';

type ViewId =
  | 'overview'
  | 'users'
  | 'registrationDefaults'
  | 'devices'
  | 'translation'
  | 'usage'
  | 'releases'
  | 'audit'
  | 'system'
  | 'admins'
  | 'account';

interface NavigationItem {
  id: ViewId;
  label: string;
  icon: any;
  permission?: AdminPermission;
  superAdminOnly?: boolean;
}

type AdminTheme = 'dark' | 'light';

const themeStorageKey = 'sky-lingo-v2-admin-theme';

const navigation: Array<{
  group: string;
  items: NavigationItem[];
}> = [
  {
    group: '总览',
    items: [
      { id: 'overview', label: '运营概览', icon: CircleGauge, permission: 'overview.read' }
    ]
  },
  {
    group: '客户',
    items: [
      { id: 'users', label: '用户管理', icon: Users, permission: 'users.read' },
      { id: 'registrationDefaults', label: '新用户默认设置', icon: SlidersHorizontal, permission: 'users.read' },
      { id: 'devices', label: '设备管理', icon: Laptop, permission: 'devices.read' }
    ]
  },
  {
    group: '服务',
    items: [
      { id: 'translation', label: '翻译服务', icon: Languages, permission: 'translation.read' },
      { id: 'usage', label: '用量统计', icon: BarChart3, permission: 'usage.read' }
    ]
  },
  {
    group: '运维',
    items: [
      { id: 'releases', label: '客户端版本', icon: Rocket, permission: 'releases.read' },
      { id: 'audit', label: '审计日志', icon: FileClock, permission: 'audit.read' },
      { id: 'system', label: '系统状态', icon: ServerCog, permission: 'system.read' }
    ]
  },
  {
    group: '权限',
    items: [
      {
        id: 'admins',
        label: '管理员管理',
        icon: UserCog,
        superAdminOnly: true
      },
      { id: 'account', label: '账号安全', icon: LockKeyhole }
    ]
  }
];

const viewMeta: Record<ViewId, { title: string; description: string }> = {
  overview: { title: '运营概览', description: '查看产品、客户与核心服务的实时状态。' },
  users: { title: '用户管理', description: '管理用户资料、有效期和翻译模型权限。' },
  registrationDefaults: { title: '新用户默认设置', description: '设置客户端新注册账号首次开通时自动使用的内容。' },
  devices: { title: '设备管理', description: '按用户查询设备，管理停用、恢复和重置绑定。' },
  translation: { title: '翻译服务', description: '配置主模型与手动备用通道，分别检测接口状态。' },
  usage: { title: '用量统计', description: '查看供应商分期计量与用户历史累计原文字符。' },
  releases: { title: '客户端版本', description: '管理稳定版、测试版与强制升级策略。' },
  audit: { title: '审计日志', description: '查看管理员操作和关键配置变化。' },
  system: { title: '系统状态', description: '检查 API、数据库、Signal 运行时和翻译服务。' },
  admins: { title: '管理员管理', description: '创建管理员并分配各业务模块的查看与管理权限。' },
  account: { title: '账号安全', description: '查看当前管理员身份并修改自己的登录密码。' }
};

const activeView = ref<ViewId>('overview');
const mobileOpen = ref(false);
const adminTheme = ref<AdminTheme>(readAdminTheme());
const loading = ref(true);
const saving = ref(false);
const errorMessage = ref('');
const notice = ref('');
const overview = ref<AdminOverview | null>(null);
const translation = ref<TranslationPublicConfig | null>(null);
const currentAdmin = ref<AdminAccount | null>(null);
const admins = ref<AdminAccount[]>([]);
const loginRequired = ref(!getAdminToken());
const loginForm = reactive({
  username: 'admin',
  password: ''
});
const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});
const adminForm = reactive({
  username: '',
  displayName: '',
  password: '',
  permissions: [] as AdminPermission[]
});
const editingAdminId = ref<number | null>(null);
const adminEditForm = reactive({
  displayName: '',
  status: 'active' as AdminAccountStatus,
  permissions: [] as AdminPermission[],
  newPassword: ''
});
const keyInputs = reactive(createProviderInputs());
const testState = reactive<Record<TranslationProvider, {
  loading: boolean;
  ok: boolean | null;
  message: string;
}>>(Object.fromEntries(providerIds.map(id => [id, { loading: false, ok: null, message: '' }])) as Record<TranslationProvider, { loading: boolean; ok: boolean | null; message: string }>);
const viewLoadCount = ref(0);
const viewLoading = computed(() => viewLoadCount.value > 0);
const searchQuery = ref('');
const deviceUserQuery = ref('');
const deviceUsernameMatch = ref<UsernameMatchMode>('contains');
const deviceStateFilter = ref<DeviceStateFilter>('active');
const userPage = ref(1);
const devicePage = ref(1);
const usagePage = ref(1);
const releasePage = ref(1);
const auditPage = ref(1);
const adminPage = ref(1);
const OVERVIEW_REFRESH_INTERVAL_MS = 30000;
let overviewRefreshTimer: number | undefined;
const users = ref<AdminUser[]>([]);
const registrationDefaults = ref<RegistrationDefaults | null>(null);
const licenseUser = ref<AdminUser | null>(null);
const userAccess = ref<AdminLicense | null>(null);
const accessLoaded = ref(false);
const licenseLoading = ref(false);
const licenseSaving = ref(false);
const licenseError = ref('');
const licenseNotice = ref('');
let licenseRequestGeneration = 0;
const devices = ref<AdminDevice[]>([]);
const usage = ref<AdminUserUsageSummary[]>([]);
const translationUsage = ref<AdminTranslationUsageSummary | null>(null);
const usagePeriod = ref<UsagePeriodKey>('today');
const usageSummaryError = ref('');
const releases = ref<AdminClientRelease[]>([]);
const auditLogs = ref<AdminAuditLog[]>([]);
const userForm = reactive({
  username: '',
  password: ''
});
const registrationDefaultsForm = reactive({
  trialDays: 90,
  maxDevices: 3,
  providers: ['google', 'deepl'] as TranslationProvider[],
  translationProvider: 'google' as TranslationProvider
});
const releaseForm = reactive({
  version: '',
  channel: 'stable' as ReleaseChannel,
  updateType: 'resource' as ClientUpdateType,
  manifestUrl: '',
  installerUrl: '',
  releaseNotes: '',
  mandatory: false,
  published: false
});

const permissionGroups: Array<{
  label: string;
  read: AdminPermission;
  write?: AdminPermission;
}> = [
  { label: '运营概览', read: 'overview.read' },
  { label: '用户管理', read: 'users.read', write: 'users.write' },
  { label: '用户权限与有效期', read: 'licenses.read', write: 'licenses.write' },
  { label: '设备管理', read: 'devices.read', write: 'devices.write' },
  { label: '翻译服务', read: 'translation.read', write: 'translation.write' },
  { label: '用量统计', read: 'usage.read' },
  { label: '客户端版本', read: 'releases.read', write: 'releases.write' },
  { label: '审计日志', read: 'audit.read' },
  { label: '系统状态', read: 'system.read' }
];

const currentMeta = computed(() => viewMeta[activeView.value]);
const databaseReady = computed(() => overview.value?.database.connected ?? false);
const isSuperAdmin = computed(() => currentAdmin.value?.role === 'super_admin');
const visibleNavigation = computed(() => navigation
  .map((group) => ({
    ...group,
    items: group.items.filter((item) => canAccessItem(item))
  }))
  .filter((group) => group.items.length));
const normalizedSearch = computed(() => searchQuery.value.trim().toLowerCase());
const filteredUsers = computed(() => users.value.filter((user) => matchesSearch(
  user.username,
  user.status
)));
const userMatchedDevices = computed(() => filterUsersByUsername(devices.value, deviceUserQuery.value, deviceUsernameMatch.value).filter((device) => matchesSearch(
  device.username,
  device.deviceName,
  device.deviceFingerprint
)));
const deviceStateCounts = computed(() => countDeviceStates(userMatchedDevices.value));
const filteredDevices = computed(() => filterDevicesByState(userMatchedDevices.value, deviceStateFilter.value));
const userPagination = computed(() => paginateAdminRows(filteredUsers.value, userPage.value));
const devicePagination = computed(() => paginateAdminRows(filteredDevices.value, devicePage.value));
watch(() => userPagination.value.page, page => { userPage.value = page; });
watch(() => devicePagination.value.page, page => { devicePage.value = page; });
const filteredUsage = computed(() => usage.value.filter((record) => matchesSearch(
  record.username,
  String(record.totalCharacters)
)));
const filteredReleases = computed(() => releases.value.filter((release) => matchesSearch(
  release.version,
  release.channel,
  release.manifestUrl
)));
const filteredAuditLogs = computed(() => auditLogs.value.filter((log) => matchesSearch(
  log.actor,
  auditActionLabel(log.action),
  auditTargetLabel(log.target),
  auditDescription(log)
)));
const filteredAdmins = computed(() => admins.value.filter((admin) => matchesSearch(
  admin.username,
  admin.displayName,
  admin.role,
  admin.status
)));
const usagePagination = computed(() => paginateAdminRows(filteredUsage.value, usagePage.value));
const releasePagination = computed(() => paginateAdminRows(filteredReleases.value, releasePage.value));
const auditPagination = computed(() => paginateAdminRows(filteredAuditLogs.value, auditPage.value));
const adminPagination = computed(() => paginateAdminRows(filteredAdmins.value, adminPage.value));
function resetTablePages(): void {
  userPage.value = 1;
  devicePage.value = 1;
  usagePage.value = 1;
  releasePage.value = 1;
  auditPage.value = 1;
  adminPage.value = 1;
}
watch(searchQuery, resetTablePages, { flush: 'sync' });
watch([deviceUserQuery, deviceUsernameMatch, deviceStateFilter], () => { devicePage.value = 1; }, { flush: 'sync' });
watch(() => usagePagination.value.page, page => { usagePage.value = page; });
watch(() => releasePagination.value.page, page => { releasePage.value = page; });
watch(() => auditPagination.value.page, page => { auditPage.value = page; });
watch(() => adminPagination.value.page, page => { adminPage.value = page; });

onMounted(() => {
  applyAdminTheme(adminTheme.value);
  void loadAll();
});

onBeforeUnmount(() => {
  licenseRequestGeneration++;
  stopOverviewAutoRefresh();
});

function readAdminTheme(): AdminTheme {
  try {
    return window.localStorage.getItem(themeStorageKey) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function toggleAdminTheme(): void {
  adminTheme.value = adminTheme.value === 'dark' ? 'light' : 'dark';
  applyAdminTheme(adminTheme.value);
  try {
    window.localStorage.setItem(themeStorageKey, adminTheme.value);
  } catch {
    // 主题仍会在当前页面生效。
  }
}

function applyAdminTheme(theme: AdminTheme): void {
  document.documentElement.dataset.adminTheme = theme;
}

async function loadAll(): Promise<void> {
  if (!getAdminToken()) {
    loginRequired.value = true;
    loading.value = false;
    return;
  }
  loading.value = true;
  errorMessage.value = '';
  try {
    currentAdmin.value = (
      await api<{ admin: AdminAccount }>('/api/admin/me')
    ).admin;
    ensureAccessibleView();
    await loadViewData(activeView.value, false);
    loginRequired.value = false;
    startOverviewAutoRefresh();
  } catch (error) {
    handleError(error);
  } finally {
    loading.value = false;
  }
}

function selectView(view: ViewId): void {
  if (!canAccessView(view) || licenseSaving.value) return;
  closeUserLicenses();
  activeView.value = view;
  mobileOpen.value = false;
  searchQuery.value = '';
  resetTablePages();
  void loadViewData(view);
}

async function loadViewData(view: ViewId, showLoading = true): Promise<void> {
  if (showLoading) viewLoadCount.value++;
  errorMessage.value = '';
  try {
    if (view === 'overview' || view === 'system') {
      overview.value = await api<AdminOverview>('/api/admin/overview');
    } else if (view === 'registrationDefaults') {
      applyRegistrationDefaults((await api<{ defaults: RegistrationDefaults }>(
        '/api/admin/registration-defaults'
      )).defaults);
    } else if (view === 'translation') {
      translation.value = await api<TranslationPublicConfig>(
        '/api/admin/translation-config'
      );
    } else {
      await loadBusinessView(view, false);
    }
  } catch (error) {
    handleError(error);
  } finally {
    if (showLoading) viewLoadCount.value--;
  }
}

async function loadBusinessView(view: ViewId, showLoading = true): Promise<void> {
  if (!['users', 'devices', 'usage', 'releases', 'audit', 'admins'].includes(view)) {
    return;
  }
  if (showLoading) viewLoadCount.value++;
  errorMessage.value = '';
  try {
    if (view === 'users') {
      users.value = await readAllAdminRows(async (page, pageSize) =>
        (await api<{ users: AdminUser[] }>(`/api/admin/users?page=${page}&pageSize=${pageSize}`)).users);
    } else if (view === 'devices') {
      devices.value = await readAllAdminRows(async (page, pageSize) =>
        (await api<{ devices: AdminDevice[] }>(`/api/admin/devices?page=${page}&pageSize=${pageSize}`)).devices);
    } else if (view === 'usage') {
      const result = await readUsageView(api, can('usage.read'));
      usage.value = result.usage;
      if (result.summary) translationUsage.value = result.summary;
      usageSummaryError.value = result.summaryError;
    } else if (view === 'releases') {
      releases.value = await readAllAdminRows(async (page, pageSize) =>
        (await api<{ releases: AdminClientRelease[] }>(`/api/admin/releases?page=${page}&pageSize=${pageSize}`)).releases);
    } else if (view === 'audit') {
      auditLogs.value = await readAllAdminRows(async (page, pageSize) =>
        (await api<{ auditLogs: AdminAuditLog[] }>(`/api/admin/audit?page=${page}&pageSize=${pageSize}`)).auditLogs);
    } else if (view === 'admins') {
      admins.value = await readAllAdminRows(async (page, pageSize) =>
        (await api<{ admins: AdminAccount[] }>(`/api/admin/admins?page=${page}&pageSize=${pageSize}`)).admins);
    }
  } catch (error) {
    handleError(error);
  } finally {
    if (showLoading) viewLoadCount.value--;
  }
}

async function saveTranslation(): Promise<void> {
  if (!translation.value) return;
  saving.value = true;
  notice.value = '';
  errorMessage.value = '';
  try {
    const payload = buildTranslationUpdate(translation.value, keyInputs);
    translation.value = await api<TranslationPublicConfig>(
      '/api/admin/translation-config',
      { method: 'PUT', body: JSON.stringify(payload) }
    );
    Object.assign(keyInputs, createProviderInputs());
    notice.value = '翻译配置已加密保存';
    if (overview.value) overview.value.translation = translation.value;
  } catch (error) {
    handleError(error);
  } finally {
    saving.value = false;
  }
}

async function testProvider(provider: TranslationProvider): Promise<void> {
  const state = testState[provider];
  state.loading = true;
  state.ok = null;
  state.message = '';
  try {
    const result = await api<{
      translatedText: string;
      model: string;
    }>('/api/admin/translation-check', {
      method: 'POST',
      body: JSON.stringify({ provider })
    });
    state.ok = true;
    state.message = `${result.translatedText} · ${result.model}`;
  } catch (error) {
    state.ok = false;
    state.message = friendlyError(error);
  } finally {
    state.loading = false;
  }
}

async function checkUsageProvider(provider: TranslationProvider): Promise<void> {
  await testProvider(provider);
  await loadBusinessView('usage', false);
}

async function checkAllUsageProviders(): Promise<void> {
  for (const provider of providerIds) await testProvider(provider);
  await loadBusinessView('usage', false);
}

async function createUser(): Promise<void> {
  await mutate('用户已创建', async () => {
    await api('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userForm)
    });
    userForm.username = '';
    userForm.password = '';
    await refreshBusiness('users');
  });
}

function applyRegistrationDefaults(value: RegistrationDefaults): void {
  registrationDefaults.value = value;
  registrationDefaultsForm.trialDays = value.trialDays;
  registrationDefaultsForm.maxDevices = value.maxDevices;
  registrationDefaultsForm.providers = [...value.providers];
  registrationDefaultsForm.translationProvider = value.translationProvider;
}

function ensureRegistrationDefaultProvider(): void {
  registrationDefaultsForm.translationProvider = defaultRegistrationProvider(
    registrationDefaultsForm.providers, registrationDefaultsForm.translationProvider
  ) || 'google';
}

async function saveRegistrationDefaults(): Promise<void> {
  if (!registrationDefaults.value || viewLoading.value || loading.value) {
    errorMessage.value = '请先读取新用户默认设置后再保存';
    return;
  }
  if (!registrationDefaultsForm.providers.length) {
    errorMessage.value = '请至少选择一种可用翻译服务';
    return;
  }
  ensureRegistrationDefaultProvider();
  if (!defaultRegistrationProvider(registrationDefaultsForm.providers, registrationDefaultsForm.translationProvider)) {
    errorMessage.value = '请至少允许一种主模型作为新用户默认服务；备用通道仅支持用户手动切换';
    return;
  }
  await mutate('新注册用户默认设置已保存', async () => {
    const result = await api<{ defaults: RegistrationDefaults }>('/api/admin/registration-defaults', {
      method: 'PUT',
      body: JSON.stringify(registrationDefaultsForm)
    });
    applyRegistrationDefaults(result.defaults);
  });
}

async function toggleUser(user: AdminUser): Promise<void> {
  const status: UserStatus = user.status === 'active' ? 'disabled' : 'active';
  await mutate(status === 'active' ? '用户已启用' : '用户已停用', async () => {
    await api(`/api/admin/users/${user.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
    await refreshBusiness('users');
  });
}

async function resetUserPassword(user: AdminUser): Promise<void> {
  const password = window.prompt(`为 ${user.username} 设置新密码（至少 6 位，包含字母和数字）`);
  if (!password) return;
  await mutate('用户密码已重置，原客户端会话已失效', async () => {
    await api(`/api/admin/users/${user.id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword: password })
    });
  });
}

function closeUserLicenses(force = false): void {
  if (licenseSaving.value && !force) return;
  licenseRequestGeneration++;
  licenseUser.value = null;
  userAccess.value = null;
  accessLoaded.value = false;
  licenseLoading.value = false;
  licenseError.value = '';
  licenseNotice.value = '';
}

async function openUserLicenses(user: AdminUser): Promise<void> {
  if (!(can('licenses.read') || can('licenses.write')) || licenseSaving.value) return;
  closeUserLicenses();
  licenseUser.value = user;
  await refreshUserLicenses();
}

async function refreshUserLicenses(): Promise<void> {
  const user = licenseUser.value;
  if (!user || !can('licenses.read')) return;
  const generation = ++licenseRequestGeneration;
  licenseLoading.value = true;
  licenseError.value = '';
  try {
    const access = await readUserAccess(api, user.id);
    if (generation === licenseRequestGeneration && licenseUser.value?.id === user.id) { userAccess.value = access; accessLoaded.value = true; }
  } catch (error) {
    if (generation !== licenseRequestGeneration) return;
    licenseError.value = friendlyError(error);
    if (error instanceof ApiError && error.status === 401) handleError(error);
  } finally {
    if (generation === licenseRequestGeneration) licenseLoading.value = false;
  }
}

async function mutateUserLicense(payload: AccessUpdate): Promise<void> {
  const user = licenseUser.value;
  if (!user || !can('licenses.write') || licenseSaving.value || licenseLoading.value) return;
  licenseSaving.value = true;
  licenseError.value = '';
  licenseNotice.value = '';
  try {
    const access = await saveUserAccess(api, user.id, payload);
    if (licenseUser.value?.id !== user.id) return;
    // Use the confirmed write response directly; no new history record is created.
    userAccess.value = access;
    accessLoaded.value = true;
    licenseNotice.value = '权限与有效期已保存';
  } catch (error) {
    licenseError.value = friendlyError(error);
    if (error instanceof ApiError && error.status === 401) handleError(error);
  } finally {
    licenseSaving.value = false;
  }
}

async function toggleDevice(device: AdminDevice): Promise<void> {
  if (device.resetAt) return;
  const revoked = !device.revokedAt;
  await mutate(revoked ? '设备已停用' : '设备已恢复', async () => {
    await api(`/api/admin/devices/${device.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ revoked })
    });
    await refreshBusiness('devices');
  });
}

async function resetDevice(device: AdminDevice): Promise<void> {
  if (saving.value || device.resetAt) return;
  const confirmed = window.confirm(
    `确认重置用户“${device.username}”的设备“${device.deviceName || '未命名设备'}”（#${device.id}）？\n\n`
    + '这会使该设备的旧登录会话失效，并解除原绑定。用户可以重新登录绑定，每个账号最多绑定 3 台电脑。\n'
    + '账号套餐、翻译用量及本地聊天不会清空。此记录将保留为“已重置”，无法通过“恢复”重新启用。'
  );
  if (!confirmed) return;
  await mutate('设备绑定已重置，用户可重新登录绑定', async () => {
    await api(`/api/admin/devices/${device.id}/reset`, { method: 'POST' });
    await refreshBusiness('devices');
  });
}

async function createRelease(): Promise<void> {
  await mutate('客户端版本已创建', async () => {
    await api('/api/admin/releases', {
      method: 'POST',
      body: JSON.stringify(releaseForm)
    });
    releaseForm.version = '';
    releaseForm.updateType = 'resource';
    releaseForm.manifestUrl = '';
    releaseForm.installerUrl = '';
    releaseForm.releaseNotes = '';
    releaseForm.mandatory = false;
    releaseForm.published = false;
    await refreshBusiness('releases');
  });
}

async function toggleRelease(release: AdminClientRelease): Promise<void> {
  const published = !release.publishedAt;
  await mutate(published ? '版本已发布' : '版本已转为草稿', async () => {
    await api(`/api/admin/releases/${release.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ published })
    });
    await refreshBusiness('releases');
  });
}

async function createAdmin(): Promise<void> {
  await mutate('管理员已创建', async () => {
    await api('/api/admin/admins', {
      method: 'POST',
      body: JSON.stringify(adminForm)
    });
    adminForm.username = '';
    adminForm.displayName = '';
    adminForm.password = '';
    adminForm.permissions = [];
    await loadBusinessView('admins', false);
  });
}

function startEditAdmin(admin: AdminAccount): void {
  if (admin.role === 'super_admin') return;
  editingAdminId.value = admin.id;
  adminEditForm.displayName = admin.displayName;
  adminEditForm.status = admin.status;
  adminEditForm.permissions = [...admin.permissions];
  adminEditForm.newPassword = '';
  void nextTick(() => {
    document.getElementById('admin-edit-panel')?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
    document.getElementById('admin-edit-display-name')?.focus({ preventScroll: true });
  });
}

function cancelEditAdmin(): void {
  editingAdminId.value = null;
  adminEditForm.newPassword = '';
}

async function updateSelectedAdmin(): Promise<void> {
  if (!editingAdminId.value) return;
  await mutate('管理员权限已更新', async () => {
    await api(`/api/admin/admins/${editingAdminId.value}`, {
      method: 'PATCH',
      body: JSON.stringify({
        displayName: adminEditForm.displayName,
        status: adminEditForm.status,
        permissions: adminEditForm.permissions
      })
    });
    await loadBusinessView('admins', false);
    cancelEditAdmin();
  });
}

async function resetSelectedAdminPassword(): Promise<void> {
  if (!editingAdminId.value) return;
  await mutate('管理员密码已重置，原会话已失效', async () => {
    await api(`/api/admin/admins/${editingAdminId.value}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword: adminEditForm.newPassword })
    });
    adminEditForm.newPassword = '';
  });
}

async function changePassword(): Promise<void> {
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    errorMessage.value = friendlyError(new Error('password_confirmation_mismatch'));
    return;
  }
  await mutate('密码已修改，其他登录会话已失效', async () => {
    const result = await api<AdminLoginResult>('/api/admin/password', {
      method: 'PUT',
      body: JSON.stringify({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      })
    });
    setAdminToken(result.token);
    currentAdmin.value = result.admin;
    passwordForm.currentPassword = '';
    passwordForm.newPassword = '';
    passwordForm.confirmPassword = '';
  });
}

async function mutate(message: string, action: () => Promise<void>): Promise<void> {
  saving.value = true;
  notice.value = '';
  errorMessage.value = '';
  try {
    await action();
    notice.value = message;
  } catch (error) {
    handleError(error);
  } finally {
    saving.value = false;
  }
}

async function refreshBusiness(view: ViewId): Promise<void> {
  const tasks: Array<Promise<unknown>> = [loadBusinessView(view, false)];
  if (can('overview.read') || can('system.read')) {
    tasks.push(api<AdminOverview>('/api/admin/overview').then((value) => {
      overview.value = value;
    }));
  }
  await Promise.all(tasks);
}

async function submitLogin(): Promise<void> {
  saving.value = true;
  errorMessage.value = '';
  try {
    const result = await api<AdminLoginResult>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify(loginForm)
    });
    setAdminToken(result.token);
    currentAdmin.value = result.admin;
    loginForm.password = '';
    loginRequired.value = false;
    ensureAccessibleView();
    await loadViewData(activeView.value, false);
    startOverviewAutoRefresh();
  } catch (error) {
    errorMessage.value = friendlyError(error);
  } finally {
    saving.value = false;
    loading.value = false;
  }
}

async function logout(): Promise<void> {
  try {
    await api('/api/admin/logout', { method: 'POST' });
  } catch {
    // 本地会话仍然需要清理。
  }
  closeUserLicenses(true);
  setAdminToken('');
  currentAdmin.value = null;
  overview.value = null;
  translation.value = null;
  loginRequired.value = true;
  stopOverviewAutoRefresh();
}

function startOverviewAutoRefresh(): void {
  stopOverviewAutoRefresh();
  if (!(can('overview.read') || can('system.read'))) return;
  overviewRefreshTimer = window.setInterval(() => {
    void refreshOverview();
  }, OVERVIEW_REFRESH_INTERVAL_MS);
}

function stopOverviewAutoRefresh(): void {
  if (overviewRefreshTimer) window.clearInterval(overviewRefreshTimer);
  overviewRefreshTimer = undefined;
}

async function refreshOverview(): Promise<void> {
  if (loginRequired.value || !(can('overview.read') || can('system.read'))) return;
  try {
    overview.value = await api<AdminOverview>('/api/admin/overview');
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) handleError(error);
  }
}

function handleError(error: unknown): void {
  if (error instanceof ApiError && error.status === 401) {
    closeUserLicenses(true);
    setAdminToken('');
    currentAdmin.value = null;
    loginRequired.value = true;
    return;
  }
  errorMessage.value = friendlyError(error);
}

function friendlyError(error: unknown): string {
  const message = error instanceof Error ? error.message : 'request_failed';
  const labels: Record<string, string> = {
    google_api_key_not_configured: '尚未配置 Google API 密钥',
    deepl_api_key_not_configured: '尚未配置 DeepL API 密钥',
    google_provider_disabled: 'Google 翻译线路已停用',
    deepl_provider_disabled: 'DeepL 翻译线路已停用',
    default_provider_must_be_enabled: '当前服务正在使用该模型，请保持启用',
    fallback_provider_must_be_enabled: '当前服务正在使用该模型，请保持启用',
    admin_list_pagination_unavailable: '列表分页读取异常，请刷新后重试',
    invalid_project_id: '项目 ID 必须是大于或等于 0 的整数',
    invalid_model: '模型名称只能包含字母、数字、点、短横线、下划线或冒号',
    invalid_region: '地域名称格式无效',
    invalid_base_url: '接口地址必须是 HTTPS 地址，且不包含用户名、密码或查询参数',
    invalid_signed_provider_base_url: '此模型的 API 根地址不能包含路径，请使用服务域名',
    admin_unauthorized: '管理会话已失效，请重新登录',
    admin_forbidden: '当前管理员没有此操作权限',
    super_admin_required: '只有超级管理员可以管理其他管理员',
    invalid_admin_credentials: '管理员账号或密码错误',
    invalid_current_password: '当前密码不正确',
    weak_admin_password: '密码至少 10 位，并同时包含字母和数字',
    invalid_admin_username: '账号只能使用 3–32 位字母、数字、点、下划线或短横线',
    admin_username_exists: '该管理员账号已经存在',
    admin_not_found: '管理员不存在',
    super_admin_protected: '超级管理员不可停用、降权或重置',
    invalid_admin_permissions: '管理员权限配置无效',
    invalid_admin_status: '管理员状态无效',
    password_confirmation_mismatch: '两次输入的新密码不一致',
    database_not_configured: '业务数据库尚未配置',
    invalid_email: '请输入有效的邮箱地址',
    user_email_exists: '该邮箱已经存在',
    user_not_found: '用户不存在',
    invalid_user_status: '用户状态无效',
    invalid_client_credentials: '用户密码无效',
    weak_client_password: '用户密码至少 6 位，并同时包含字母和数字',
    invalid_provider_policy: '请至少选择一个翻译供应商',
    backup_provider_manual_only: '备用通道仅支持用户手动切换，不能设为主模型或自动兜底',
    provider_access_restricted: '公共翻译通道暂时拒绝访问，请切换其他模型，或由管理员手动检测恢复',
    invalid_expiry: '授权到期时间必须晚于当前时间',
    license_not_found: '当前权限配置不存在或不属于当前用户',
    current_access_missing_fields: '首次设置请完整填写模型权限和到期时间',
    current_access_multiple_records: '当前配置存在重复，请联系管理员处理',
    access_owner_mismatch: '当前配置与所选用户不一致，请刷新重试',
    invalid_access_owner: '用户标识无效，请刷新后重试',
    access_response_invalid: '当前配置读取异常，请刷新重试',
    license_owner_mismatch: '套餐所属用户与当前用户不一致，请刷新后重试',
    invalid_license_owner: '用户或套餐标识无效',
    invalid_license_id: '套餐标识无效，请刷新后重试',
    invalid_license_update: '请先修改需要保存的配置',
    database_unavailable: '权限服务暂时不可用，请稍后重试',
    invalid_license_status: '授权状态无效',
    device_not_found: '设备不存在',
    device_binding_reset: '该设备绑定已重置，请让用户重新登录绑定',
    device_limit_reached: '设备绑定名额已满，请先重置不再使用的设备；每个账号最多 3 台电脑',
    invalid_max_devices: '每个账号的设备上限只能设置为 1–3 台',
    release_version_exists: '该版本和发布通道已经存在',
    invalid_version: '版本号格式应为 1.2.3',
    invalid_update_type: '更新方式无效',
    invalid_manifest_url: '请输入有效的清单地址',
    installer_url_required: '请输入安装包地址',
    release_notes_required: '请输入版本说明',
    request_failed: '请求失败，请检查中央 API'
  };
  if (labels[message]) return labels[message];
  const provider = providerIds.find(id => message.startsWith(`${id}_`));
  if (provider && message.endsWith('_provider_disabled')) return `${providerName(provider)}已停用`;
  if (provider && message.endsWith('_not_configured')) return `${providerName(provider)}尚未配置完整凭据`;
  return `接口检测失败：${message}`;
}

function can(permission: AdminPermission): boolean {
  const admin = currentAdmin.value;
  return Boolean(
    admin
    && (
      admin.role === 'super_admin'
      || admin.permissions.includes(permission)
    )
  );
}

function canAccessItem(item: NavigationItem): boolean {
  if (item.id === 'users') return canOpenUserManagement(can);
  const admin = currentAdmin.value;
  if (!admin) return false;
  if (item.superAdminOnly) return admin.role === 'super_admin';
  return !item.permission || can(item.permission);
}

function canAccessView(view: ViewId): boolean {
  const item = navigation
    .flatMap((group) => group.items)
    .find((candidate) => candidate.id === view);
  return Boolean(item && canAccessItem(item));
}

function ensureAccessibleView(): void {
  if (canAccessView(activeView.value)) return;
  activeView.value = visibleNavigation.value[0]?.items[0]?.id || 'account';
}

function adminRoleLabel(admin: AdminAccount | null): string {
  return admin?.role === 'super_admin' ? '超级管理员' : '管理员';
}

function adminInitials(admin: AdminAccount | null): string {
  return (admin?.displayName || admin?.username || 'A').slice(0, 2).toUpperCase();
}

function permissionSummary(admin: AdminAccount): string {
  if (admin.role === 'super_admin') return '全部权限';
  return `${admin.permissions.filter((permission) => permission.endsWith('.read')).length} 个模块`;
}

function providerName(provider: TranslationProvider): string {
  return `${providerDetails[provider].label}${providerDetails[provider].backup ? '（备用）' : ''}`;
}

function formatCount(value: number | undefined): string {
  return new Intl.NumberFormat('zh-CN').format(value || 0);
}

function dateTime(value: string | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value));
}

function futureLocalDate(days: number): string {
  const date = new Date(Date.now() + days * 86_400_000);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function matchesSearch(...values: Array<string | number | null | undefined>): boolean {
  const query = normalizedSearch.value;
  return !query || values.some((value) => String(value ?? '').toLowerCase().includes(query));
}

function statusLabel(value: UserStatus | LicenseStatus): string {
  const labels: Record<UserStatus | LicenseStatus, string> = {
    active: '正常',
    disabled: '已停用',
    expired: '已过期',
    revoked: '已撤销'
  };
  return labels[value];
}

function auditActionLabel(action: string): string {
  const labels: Record<string, string> = {
    'translation-config.update': '修改翻译设置',
    'registration-defaults.update': '修改新用户默认设置',
    'release.create': '创建客户端版本',
    'release.update': '修改客户端版本',
    'admin.password.change': '修改自己的密码',
    'admin.create': '创建管理员',
    'admin.update': '修改管理员',
    'admin.password.reset': '重置管理员密码',
    'user.create': '创建用户',
    'user.update': '修改用户',
    'user.password.reset': '重置用户密码',
    'license.create': '创建授权',
    'license.update': '修改授权',
    'device.update': '修改设备',
    'device.reset': '重置设备绑定',
    'client.register': '用户注册客户端'
  };
  return labels[action] || '管理操作';
}

function auditTargetLabel(target: string): string {
  if (target === 'translation-config') return '翻译服务设置';
  if (target === 'registration-defaults') return '新用户默认设置';
  const match = /^(user|license|device|release|admin):(\d+)$/.exec(target);
  if (!match) return '系统记录';
  const labels: Record<string, string> = {
    user: '用户',
    license: '授权',
    device: '设备',
    release: '客户端版本',
    admin: '管理员'
  };
  return `${labels[match[1]] || '记录'}（编号 ${match[2]}）`;
}

function auditDescription(log: AdminAuditLog): string {
  const metadata = log.metadata || {};
  const status = typeof metadata.status === 'string' ? metadata.status : null;
  const version = typeof metadata.version === 'string' ? metadata.version : null;
  const published = typeof metadata.published === 'boolean' ? metadata.published : null;

  switch (log.action) {
    case 'translation-config.update':
      return '已保存翻译服务设置';
    case 'registration-defaults.update':
      return '已保存新用户默认设置';
    case 'release.create':
      return version
        ? `已创建客户端版本 ${version}${published ? '，并已发布' : ''}`
        : '已创建客户端版本';
    case 'release.update':
      return published === null ? '已修改客户端版本' : (published ? '已发布客户端版本' : '已取消发布客户端版本');
    case 'admin.password.change':
      return '已修改自己的登录密码';
    case 'admin.create':
      return '已创建管理员账号';
    case 'admin.update':
      return status === 'disabled' ? '已停用管理员账号' : '已修改管理员权限或状态';
    case 'admin.password.reset':
      return '已重置管理员登录密码';
    case 'user.create':
      return '已创建用户账号';
    case 'user.update':
      return status === 'disabled' ? '已停用用户账号' : '已修改用户资料或状态';
    case 'user.password.reset':
      return '已重置用户登录密码';
    case 'license.create':
      return '已创建用户授权';
    case 'license.update':
      return status === 'revoked' ? '已撤销用户授权' : '已修改用户授权状态';
    case 'device.update':
      return metadata.revoked === true ? '已停用设备' : '已恢复设备';
    case 'device.reset':
      return '已解除设备绑定并使旧登录会话失效';
    case 'client.register':
      return '用户已完成客户端注册';
    default:
      return '已完成管理操作';
  }
}
</script>

<template>
  <div class="appShell" :class="{ lightTheme: adminTheme === 'light' }">
    <aside class="sidebar" :class="{ open: mobileOpen }">
      <div class="brand">
        <div class="brandMark"><Languages :size="22" /></div>
        <div>
          <strong>SKY Lingo</strong>
          <span>V2 CONTROL</span>
        </div>
        <button class="mobileClose" aria-label="关闭菜单" @click="mobileOpen = false">
          <X :size="20" />
        </button>
      </div>

      <nav>
        <section v-for="group in visibleNavigation" :key="group.group" class="navGroup">
          <p>{{ group.group }}</p>
          <button
            v-for="item in group.items"
            :key="item.id"
            :class="{ active: activeView === item.id }"
            @click="selectView(item.id)"
          >
            <component :is="item.icon" :size="18" />
            <span>{{ item.label }}</span>
            <ChevronRight v-if="activeView === item.id" class="navArrow" :size="15" />
          </button>
        </section>
      </nav>

      <div class="sidebarStatus">
        <div class="statusLine">
          <span class="pulseDot" :class="{ offline: overview && !databaseReady }"></span>
          <div>
            <strong>{{ overview ? (databaseReady ? '服务正常' : '数据库异常') : '管理员已登录' }}</strong>
            <small>API v{{ overview?.product.version || '0.1.0' }}</small>
          </div>
        </div>
        <button class="logoutButton" @click="logout">
          <LogOut :size="16" /> 退出管理
        </button>
      </div>
    </aside>

    <button
      v-if="mobileOpen"
      class="sidebarBackdrop"
      aria-label="关闭菜单"
      @click="mobileOpen = false"
    ></button>

    <main class="mainArea">
      <header class="topbar">
        <div class="titleBlock">
          <button class="menuButton" aria-label="打开菜单" @click="mobileOpen = true">
            <Menu :size="21" />
          </button>
          <div>
            <h1>{{ currentMeta.title }}</h1>
            <p>{{ currentMeta.description }}</p>
          </div>
        </div>
        <div class="topbarActions">
          <button
            class="themeToggle"
            :aria-label="adminTheme === 'dark' ? '切换到白天主题' : '切换到深色主题'"
            :title="adminTheme === 'dark' ? '切换到白天主题' : '切换到深色主题'"
            @click="toggleAdminTheme"
          >
            <Sun v-if="adminTheme === 'dark'" :size="17" />
            <Moon v-else :size="17" />
            <span>{{ adminTheme === 'dark' ? '白天主题' : '深色主题' }}</span>
          </button>
          <label class="searchBox">
            <Search :size="17" />
            <input v-model="searchQuery" type="search" placeholder="搜索当前页面" />
          </label>
          <button class="iconButton" aria-label="刷新" :disabled="loading" @click="loadAll">
            <RefreshCw :size="18" :class="{ spinning: loading }" />
          </button>
          <button class="adminBadge" @click="selectView('account')">
            <span>{{ adminInitials(currentAdmin) }}</span>
            <div>
              <strong>{{ currentAdmin?.displayName || currentAdmin?.username || '管理员' }}</strong>
              <small>{{ adminRoleLabel(currentAdmin) }}</small>
            </div>
          </button>
        </div>
      </header>

      <div class="content">
        <div v-if="errorMessage" class="alert error">{{ errorMessage }}</div>
        <div v-if="notice" class="alert success">{{ notice }}</div>

        <div v-if="loading && !overview" class="loadingGrid">
          <div v-for="item in 8" :key="item" class="skeleton"></div>
        </div>

        <template v-else-if="activeView === 'overview'">
          <section class="metricGrid">
            <article class="metricCard">
              <div class="metricIcon green"><Users :size="19" /></div>
              <span>用户总数</span>
              <strong>{{ formatCount(overview?.counts.users) }}</strong>
              <small>{{ overview?.database.message }}</small>
            </article>
            <article class="metricCard">
              <div class="metricIcon cyan"><BadgeCheck :size="19" /></div>
              <span>有效授权用户数</span>
              <strong>{{ formatCount(overview?.counts.activeLicenses) }}</strong>
              <small>有效且未过期，按正常用户去重</small>
            </article>
            <article class="metricCard">
              <div class="metricIcon purple"><Laptop :size="19" /></div>
              <span>实时在线设备</span>
              <strong>{{ formatCount(overview?.counts.onlineDevices) }}</strong>
              <small>最近 90 秒内活跃</small>
            </article>
            <article class="metricCard">
              <div class="metricIcon orange"><BarChart3 :size="19" /></div>
              <span>今日翻译字符</span>
              <strong>{{ formatCount(overview?.counts.translatedCharactersToday) }}</strong>
              <small>每 30 秒自动刷新</small>
            </article>
          </section>

          <section class="dashboardGrid">
            <article class="panel servicePanel">
              <div class="panelHeader">
                <div><span class="eyebrow">核心服务</span><h3>服务状态</h3></div>
                <button v-if="can('system.read')" class="textButton" @click="selectView('system')">
                  查看全部
                </button>
              </div>
              <div class="serviceRows">
                <div v-for="provider in providerIds" :key="provider" class="serviceRow">
                  <div class="serviceLogo"><ProviderLogo :provider="provider" /></div>
                  <div><strong>{{ providerName(provider) }}</strong><small>独立模型配置</small></div>
                  <span
                    class="statusPill"
                    :class="overview?.translation.providers[provider].configured ? 'ok' : 'warn'"
                  >
                    {{ overview?.translation.providers[provider].configured ? '已配置' : '待配置' }}
                  </span>
                </div>
                <div class="serviceRow">
                  <div class="serviceLogo signal"><ShieldCheck :size="18" /></div>
                  <div><strong>Signal 客户端本地运行</strong><small>账号由客户端本机管理</small></div>
                  <span class="statusPill ok">客户端本地运行</span>
                </div>
              </div>
            </article>

            <article class="panel scopePanel">
              <div class="panelHeader">
                <div><span class="eyebrow">产品边界</span><h3>V2 白名单</h3></div>
                <Boxes :size="20" />
              </div>
              <div class="scopeBlock">
                <small>聊天平台</small>
                <div class="tagRow"><span>WhatsApp</span><span>Signal</span></div>
              </div>
              <div class="scopeBlock">
                <small>翻译供应商</small>
                <div class="tagRow"><span v-for="provider in providerIds" :key="provider" class="providerLabel"><ProviderLogo :provider="provider" />{{ providerName(provider) }}</span></div>
              </div>
            </article>
          </section>
        </template>

        <template v-else-if="activeView === 'translation' && translation">
          <TranslationSettingsPanel :translation="translation" :inputs="keyInputs" :tests="testState"
            :can-write="can('translation.write')" :saving="saving" @save="saveTranslation" @test="testProvider" />
        </template>

        <template v-else-if="activeView === 'system'">
          <section class="sectionIntro">
            <div><span class="eyebrow">基础设施</span><h2>系统状态</h2></div>
            <button class="secondaryButton" @click="loadAll"><RefreshCw :size="16" />重新检测</button>
          </section>
          <section class="systemGrid">
            <article class="panel healthCard">
              <div class="healthIcon" :class="{ good: true }"><Activity :size="20" /></div>
              <div><span>中央 API</span><strong>运行中</strong><small>启动于 {{ dateTime(overview?.startedAt) }}</small></div>
            </article>
            <article class="panel healthCard">
              <div class="healthIcon" :class="{ good: databaseReady }"><Database :size="20" /></div>
              <div><span>MySQL</span><strong>{{ databaseReady ? '已连接' : '等待配置' }}</strong><small>{{ overview?.database.message }}</small></div>
            </article>
            <article class="panel healthCard">
              <div class="healthIcon good"><ShieldCheck :size="20" /></div>
              <div><span>Signal 运行方式</span><strong>客户端本地运行</strong><small>账号由客户端本机管理</small></div>
            </article>
            <article class="panel healthCard">
              <div class="healthIcon good"><CloudCog :size="20" /></div>
              <div><span>供应商范围</span><strong>{{ providerIds.length }} / {{ providerIds.length }}</strong><small>主模型与备用通道分别配置</small></div>
            </article>
          </section>
        </template>

        <template v-else-if="activeView === 'users'">
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">客户账户</span>
              <h2>用户管理</h2>
              <p>配置用户资料、有效期和可用模型权限。</p>
            </div>
            <span class="recordCount">{{ filteredUsers.length }} 条记录</span>
          </section>
          <section class="panel dataPanel">
            <form
              v-if="can('users.write')"
              class="toolbarForm userForm"
              @submit.prevent="createUser"
            >
              <label class="compactField">
                <span>用户名</span>
                <input
                  v-model="userForm.username"
                  required
                  maxlength="100"
                  autocomplete="username"
                  placeholder="6–100 位字母、数字、点、下划线或短横线"
                />
              </label>
              <label class="compactField">
                <span>登录密码</span>
                <input
                  v-model="userForm.password"
                  type="password"
                  required
                  minlength="6"
                  autocomplete="new-password"
                  placeholder="至少 6 位，含字母和数字"
                />
              </label>
              <button class="primaryButton" type="submit" :disabled="saving">
                <Plus :size="16" />创建用户
              </button>
            </form>
            <div v-if="viewLoading" class="tableLoading">正在读取用户数据…</div>
            <div v-else class="tableScroller">
              <table class="dataTable">
                <thead>
                  <tr><th>用户</th><th>状态</th><th>创建时间</th><th>更新时间</th><th>操作</th></tr>
                </thead>
                <tbody>
                  <tr v-for="user in userPagination.items" :key="user.id">
                    <td>
                      <strong>{{ user.username }}</strong>
                      <small>#{{ user.id }}</small>
                    </td>
                    <td><span class="statusPill" :class="user.status === 'active' ? 'ok' : 'warn'">{{ statusLabel(user.status) }}</span></td>
                    <td>{{ dateTime(user.createdAt) }}</td>
                    <td>{{ dateTime(user.updatedAt) }}</td>
                    <td class="userRowActions">
                      <button v-if="can('licenses.read') || can('licenses.write')" class="rowAction" :disabled="saving || licenseSaving" @click="openUserLicenses(user)"><BadgeCheck :size="14" />权限与有效期</button>
                      <button
                        v-if="can('users.write')"
                        class="rowAction"
                        :disabled="saving"
                        @click="toggleUser(user)"
                      >
                        <Ban v-if="user.status === 'active'" :size="14" />
                        <CheckCircle2 v-else :size="14" />
                        {{ user.status === 'active' ? '停用' : '启用' }}
                      </button>
                      <button
                        v-if="can('users.write')"
                        class="rowAction"
                        :disabled="saving"
                        @click="resetUserPassword(user)"
                      >
                        <KeyRound :size="14" />重置密码
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!filteredUsers.length" class="tableEmpty">暂无真实用户记录</div>
            </div>
            <PaginationBar v-if="!viewLoading" label="用户列表分页" :page="userPagination.page" :total="userPagination.total" @update:page="userPage = $event" />
          </section>
        </template>

        <template v-else-if="activeView === 'registrationDefaults'">
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">自动开通</span>
              <h2>新用户默认设置</h2>
              <p>仅影响之后在客户端注册的账号，不会修改已有用户。</p>
            </div>
            <span class="recordCount">{{ registrationDefaults ? '已保存' : '读取中' }}</span>
          </section>
          <section class="panel dataPanel">
            <form class="toolbarForm registrationDefaultsForm" @submit.prevent="saveRegistrationDefaults">
              <label class="compactField">
                <span>试用时长（天）</span>
                <input
                  v-model.number="registrationDefaultsForm.trialDays"
                  type="number"
                  min="1"
                  max="1095"
                  :disabled="!can('users.write') || saving"
                  required
                />
              </label>
              <label class="compactField">
                <span>最多设备数</span>
                <input
                  v-model.number="registrationDefaultsForm.maxDevices"
                  type="number"
                  min="1"
                  max="3"
                  :disabled="!can('users.write') || saving"
                  required
                />
              </label>
              <div class="checkGroup">
                <span>可用翻译服务</span>
                <label v-for="provider in providerIds" :key="provider">
                  <input
                    v-model="registrationDefaultsForm.providers"
                    type="checkbox"
                    :value="provider"
                    :disabled="!can('users.write') || saving"
                    @change="ensureRegistrationDefaultProvider"
                  /><ProviderLogo :provider="provider" />{{ providerName(provider) }}
                </label>
              </div>
              <div class="compactField">
                <span>默认翻译服务</span>
                <div class="providerDefaultOptions" role="group" aria-label="默认翻译服务">
                  <label v-for="provider in primaryProviderIds" :key="provider" :class="{ selected: registrationDefaultsForm.translationProvider === provider, unavailable: !registrationDefaultsForm.providers.includes(provider) }">
                    <input v-model="registrationDefaultsForm.translationProvider" type="radio" name="registration-default-provider" :value="provider" :disabled="!can('users.write') || saving || !registrationDefaultsForm.providers.includes(provider)" />
                    <ProviderLogo :provider="provider" />{{ providerName(provider) }}
                  </label>
                </div>
              </div>
              <button v-if="can('users.write')" class="primaryButton" type="submit" :disabled="saving">
                <CheckCircle2 :size="16" />保存默认设置
              </button>
            </form>
          </section>
        </template>

        <template v-else-if="activeView === 'devices'">
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">客户端绑定</span>
              <h2>设备管理</h2>
              <p>每个账号最多绑定 3 台电脑。停用会阻止登录；重置会解除旧绑定，让用户重新登录绑定。</p>
            </div>
            <span class="recordCount">{{ filteredDevices.length }} 条记录</span>
          </section>
          <section class="panel dataPanel">
            <div class="toolbarForm deviceFilterForm" role="search" aria-label="设备用户查询">
              <label class="compactField">
                <span>按用户查询</span>
                <input
                  v-model="deviceUserQuery"
                  type="search"
                  autocomplete="off"
                  :placeholder="deviceUsernameMatch === 'exact' ? '输入完整用户名' : '输入用户名，支持部分匹配'"
                  aria-describedby="deviceUserQueryHint"
                />
              </label>
              <label class="compactField">
                <span>用户名匹配</span>
                <select v-model="deviceUsernameMatch">
                  <option value="contains">包含匹配</option>
                  <option value="exact">精确匹配</option>
                </select>
              </label>
              <label class="compactField">
                <span>设备记录</span>
                <select v-model="deviceStateFilter">
                  <option value="active">有效设备</option>
                  <option value="revoked">停用设备</option>
                  <option value="reset">重置记录</option>
                  <option value="all">全部记录</option>
                </select>
              </label>
              <div class="deviceFilterSummary" aria-live="polite">
                <span>当前查询：有效 {{ deviceStateCounts.active }} · 停用 {{ deviceStateCounts.revoked }} · 重置历史 {{ deviceStateCounts.reset }}</span>
                <p id="deviceUserQueryHint" class="deviceFilterHint">输入后实时筛选；精确匹配只显示完整用户名。重置历史保留追溯，不占绑定名额。</p>
              </div>
            </div>
            <div v-if="viewLoading" class="tableLoading">正在读取设备数据…</div>
            <div v-else class="tableScroller">
              <table class="dataTable">
                <thead>
                  <tr><th>设备</th><th>用户</th><th>指纹</th><th>最近在线</th><th>状态</th><th>操作</th></tr>
                </thead>
                <tbody>
                  <tr v-for="device in devicePagination.items" :key="device.id">
                    <td><strong>{{ device.deviceName || '未命名设备' }}</strong><small>#{{ device.id }}</small></td>
                    <td>{{ device.username }}</td>
                    <td><span v-if="device.resetAt" class="muted">已解除绑定</span><code v-else :title="device.deviceFingerprint">{{ device.deviceFingerprint.slice(0, 12) }}…</code></td>
                    <td>{{ dateTime(device.lastSeenAt || undefined) }}</td>
                    <td><span class="statusPill" :class="device.revokedAt ? 'warn' : 'ok'">{{ device.resetAt ? '已重置' : device.revokedAt ? '已停用' : '正常' }}</span></td>
                    <td>
                      <button
                        v-if="can('devices.write') && !device.resetAt"
                        class="rowAction"
                        :disabled="saving"
                        @click="toggleDevice(device)"
                      >
                        {{ device.revokedAt ? '恢复' : '停用' }}
                      </button>
                      <button v-if="can('devices.write') && !device.resetAt" class="rowAction" :disabled="saving" @click="resetDevice(device)">
                        重置绑定
                      </button>
                      <span v-if="device.resetAt" class="muted">历史记录</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!filteredDevices.length" class="tableEmpty">{{ deviceStateFilter === 'active' ? '当前查询没有有效设备，可切换“停用设备”“重置记录”或“全部记录”查看历史' : '当前状态下没有匹配的设备记录，请调整用户名、匹配方式或设备记录筛选' }}</div>
            </div>
            <PaginationBar v-if="!viewLoading" label="设备列表分页" :page="devicePagination.page" :total="devicePagination.total" @update:page="devicePage = $event" />
          </section>
        </template>

        <template v-else-if="activeView === 'usage'">
          <TranslationUsagePanel
            v-model:period-key="usagePeriod"
            :summary="translationUsage"
            :loading="viewLoading"
            :error="usageSummaryError"
            :can-check="can('translation.write')"
            :checking="providerIds.some(provider => testState[provider].loading)"
            :checks="testState"
            @check="checkUsageProvider"
            @check-all="checkAllUsageProviders"
            @refresh="loadBusinessView('usage')"
          />
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">历史业务账本</span>
              <h2>用户累计原文字符</h2>
              <p>保留每位用户全部历史翻译的原文字符累计，与上方供应商采集范围及实际扣额分开。</p>
            </div>
            <span class="recordCount">共 {{ formatCount(filteredUsage.length) }} 位用户</span>
          </section>
          <section class="panel dataPanel">
            <div v-if="viewLoading" class="tableLoading">正在读取用量数据…</div>
            <div v-else class="tableScroller">
              <table class="dataTable">
                <thead>
                  <tr><th>用户</th><th>累计原文字符</th><th>最近使用时间</th></tr>
                </thead>
                <tbody>
                  <tr v-for="record in usagePagination.items" :key="record.userId">
                    <td>{{ record.username }}</td>
                    <td><strong>{{ formatCount(record.totalCharacters) }}</strong></td>
                    <td>{{ record.lastUsedAt ? dateTime(record.lastUsedAt) : '尚未使用' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!filteredUsage.length" class="tableEmpty">暂无用户用量数据</div>
            </div>
            <PaginationBar v-if="!viewLoading" label="用户用量分页" :page="usagePagination.page" :total="usagePagination.total" @update:page="usagePage = $event" />
          </section>
        </template>

        <template v-else-if="activeView === 'releases'">
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">客户端交付</span>
              <h2>客户端版本</h2>
              <p>登记稳定版和测试版清单，并控制发布状态与强制升级策略。</p>
            </div>
            <span class="recordCount">{{ filteredReleases.length }} 条记录</span>
          </section>
          <section class="panel dataPanel">
            <form
              v-if="can('releases.write')"
              class="toolbarForm releaseForm"
              @submit.prevent="createRelease"
            >
              <label class="compactField">
                <span>版本</span>
                <input v-model="releaseForm.version" required placeholder="1.0.0" />
              </label>
              <label class="compactField">
                <span>通道</span>
                <select v-model="releaseForm.channel">
                  <option value="stable">稳定版</option>
                  <option value="beta">测试版</option>
                </select>
              </label>
              <label class="compactField">
                <span>更新方式</span>
                <select v-model="releaseForm.updateType">
                  <option value="resource">资源热更新</option>
                  <option value="installer">完整安装包</option>
                </select>
              </label>
              <label v-if="releaseForm.updateType === 'resource'" class="compactField wide">
                <span>资源清单地址</span>
                <input v-model="releaseForm.manifestUrl" type="url" required placeholder="https://…/sky-lingo-update-manifest.json" />
              </label>
              <label class="compactField wide">
                <span>{{ releaseForm.updateType === 'resource' ? '最低安装版地址（旧客户端回退）' : '安装包地址' }}</span>
                <input v-model="releaseForm.installerUrl" type="url" required placeholder="https://…/SKY-Lingo-V2-Setup.exe" />
              </label>
              <label class="compactField wide">
                <span>版本说明</span>
                <input v-model="releaseForm.releaseNotes" required maxlength="10000" placeholder="本次更新内容" />
              </label>
              <div class="checkGroup">
                <label><input v-model="releaseForm.mandatory" type="checkbox" />强制更新</label>
                <label><input v-model="releaseForm.published" type="checkbox" />立即发布</label>
              </div>
              <button class="primaryButton" type="submit" :disabled="saving">
                <Plus :size="16" />创建版本
              </button>
            </form>
            <div v-if="viewLoading" class="tableLoading">正在读取版本数据…</div>
            <div v-else class="tableScroller">
              <table class="dataTable">
                <thead>
                  <tr><th>版本</th><th>通道</th><th>策略</th><th>发布状态</th><th>创建时间</th><th>操作</th></tr>
                </thead>
                <tbody>
                  <tr v-for="release in releasePagination.items" :key="release.id">
                    <td><strong>v{{ release.version }}</strong><small class="truncate" :title="release.updateType === 'resource' ? release.manifestUrl : release.installerUrl">{{ release.updateType === 'resource' ? release.manifestUrl : release.installerUrl }}</small></td>
                    <td>{{ release.channel === 'stable' ? '稳定版' : '测试版' }}</td>
                    <td>{{ release.updateType === 'resource' ? '资源热更新' : '完整安装包' }} · {{ release.mandatory ? '强制更新' : '可选更新' }}</td>
                    <td><span class="statusPill" :class="release.publishedAt ? 'ok' : 'neutral'">{{ release.publishedAt ? '已发布' : '草稿' }}</span></td>
                    <td>{{ dateTime(release.createdAt) }}</td>
                    <td>
                      <button
                        v-if="can('releases.write')"
                        class="rowAction"
                        :disabled="saving"
                        @click="toggleRelease(release)"
                      >
                        {{ release.publishedAt ? '转为草稿' : '发布' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!filteredReleases.length" class="tableEmpty">暂无真实客户端版本记录</div>
            </div>
            <PaginationBar v-if="!viewLoading" label="客户端版本分页" :page="releasePagination.page" :total="releasePagination.total" @update:page="releasePage = $event" />
          </section>
        </template>

        <template v-else-if="activeView === 'audit'">
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">可追溯操作</span>
              <h2>审计日志</h2>
              <p>按页查看管理操作记录，密钥和密码不会写入审计内容。</p>
            </div>
            <span class="recordCount">{{ filteredAuditLogs.length }} 条记录</span>
          </section>
          <section class="panel dataPanel">
            <div v-if="viewLoading" class="tableLoading">正在读取审计日志…</div>
            <div v-else class="tableScroller">
              <table class="dataTable auditTable">
                <thead>
                  <tr><th>时间</th><th>操作人</th><th>做了什么</th><th>涉及内容</th><th>操作说明</th></tr>
                </thead>
                <tbody>
                  <tr v-for="log in auditPagination.items" :key="log.id">
                    <td>{{ dateTime(log.createdAt) }}</td>
                    <td>{{ log.actor }}</td>
                    <td>{{ auditActionLabel(log.action) }}</td>
                    <td>{{ auditTargetLabel(log.target) }}</td>
                    <td><small class="metadata" :title="auditDescription(log)">{{ auditDescription(log) }}</small></td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!filteredAuditLogs.length" class="tableEmpty">暂无管理写操作</div>
            </div>
            <PaginationBar v-if="!viewLoading" label="审计日志分页" :page="auditPagination.page" :total="auditPagination.total" @update:page="auditPage = $event" />
          </section>
        </template>

        <template v-else-if="activeView === 'admins'">
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">后台权限</span>
              <h2>管理员管理</h2>
              <p>超级管理员固定拥有全部权限；新管理员只能访问被分配的菜单和接口。</p>
            </div>
            <span class="recordCount">{{ filteredAdmins.length }} 个管理员</span>
          </section>

          <section class="adminManagementGrid">
            <article class="panel adminEditor">
              <div class="panelHeader">
                <div><span class="eyebrow">新增账号</span><h3>创建管理员</h3></div>
              </div>
              <form class="stackedForm" @submit.prevent="createAdmin">
                <label class="field">
                  <span>登录账号</span>
                  <input
                    v-model="adminForm.username"
                    required
                    minlength="3"
                    maxlength="32"
                    autocomplete="off"
                    placeholder="例如：operations"
                  />
                </label>
                <label class="field">
                  <span>显示名称</span>
                  <input
                    v-model="adminForm.displayName"
                    required
                    maxlength="120"
                    placeholder="例如：运营管理员"
                  />
                </label>
                <label class="field">
                  <span>初始密码</span>
                  <div class="inputWithIcon">
                    <KeyRound :size="16" />
                    <input
                      v-model="adminForm.password"
                      type="password"
                      required
                      minlength="10"
                      maxlength="128"
                      autocomplete="new-password"
                      placeholder="至少 10 位，包含字母和数字"
                    />
                  </div>
                </label>
                <div class="permissionHeader">
                  <strong>分配权限</strong>
                  <button
                    type="button"
                    class="textButton"
                    @click="adminForm.permissions = [...ADMIN_PERMISSIONS]"
                  >
                    全选
                  </button>
                </div>
                <div class="permissionMatrix">
                  <div v-for="group in permissionGroups" :key="group.read" class="permissionRow">
                    <strong>{{ group.label }}</strong>
                    <label>
                      <input
                        v-model="adminForm.permissions"
                        type="checkbox"
                        :value="group.read"
                      />
                      查看
                    </label>
                    <label v-if="group.write">
                      <input
                        v-model="adminForm.permissions"
                        type="checkbox"
                        :value="group.write"
                      />
                      管理
                    </label>
                  </div>
                </div>
                <button class="primaryButton full" type="submit" :disabled="saving">
                  <Plus :size="16" />创建管理员
                </button>
              </form>
            </article>

            <div class="adminListColumn">
              <article class="panel dataPanel adminListPanel">
              <div v-if="viewLoading" class="tableLoading">正在读取管理员数据…</div>
              <div v-else class="tableScroller">
                <table class="dataTable">
                  <thead>
                    <tr><th>管理员</th><th>角色</th><th>权限</th><th>状态</th><th>最近登录</th><th>操作</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="admin in adminPagination.items" :key="admin.id">
                      <td>
                        <strong>{{ admin.displayName }}</strong>
                        <small>@{{ admin.username }}</small>
                      </td>
                      <td>{{ adminRoleLabel(admin) }}</td>
                      <td>{{ permissionSummary(admin) }}</td>
                      <td>
                        <span class="statusPill" :class="admin.status === 'active' ? 'ok' : 'warn'">
                          {{ admin.status === 'active' ? '正常' : '已停用' }}
                        </span>
                      </td>
                      <td>{{ dateTime(admin.lastLoginAt || undefined) }}</td>
                      <td>
                        <button
                          v-if="admin.role !== 'super_admin'"
                          class="rowAction"
                          @click="startEditAdmin(admin)"
                        >
                          编辑权限
                        </button>
                        <span v-else class="protectedLabel">受保护</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="!filteredAdmins.length" class="tableEmpty">暂无匹配管理员</div>
              </div>
              <PaginationBar v-if="!viewLoading" label="管理员列表分页" :page="adminPagination.page" :total="adminPagination.total" @update:page="adminPage = $event" />
              </article>

              <section v-if="editingAdminId" id="admin-edit-panel" class="panel adminEditPanel">
            <div class="panelHeader">
              <div><span class="eyebrow">管理员 #{{ editingAdminId }}</span><h3>权限与状态</h3></div>
              <button class="textButton" @click="cancelEditAdmin">取消编辑</button>
            </div>
            <form class="stackedForm" @submit.prevent="updateSelectedAdmin">
              <div class="adminEditFields">
                <label class="field">
                  <span>显示名称</span>
                  <input
                    id="admin-edit-display-name"
                    v-model="adminEditForm.displayName"
                    required
                    maxlength="120"
                  />
                </label>
                <label class="field">
                  <span>账号状态</span>
                  <select v-model="adminEditForm.status">
                    <option value="active">正常</option>
                    <option value="disabled">停用</option>
                  </select>
                </label>
              </div>
              <div class="permissionMatrix">
                <div v-for="group in permissionGroups" :key="group.read" class="permissionRow">
                  <strong>{{ group.label }}</strong>
                  <label>
                    <input
                      v-model="adminEditForm.permissions"
                      type="checkbox"
                      :value="group.read"
                    />
                    查看
                  </label>
                  <label v-if="group.write">
                    <input
                      v-model="adminEditForm.permissions"
                      type="checkbox"
                      :value="group.write"
                    />
                    管理
                  </label>
                </div>
              </div>
              <button class="primaryButton" type="submit" :disabled="saving">
                保存权限与状态
              </button>
            </form>
            <form class="passwordResetRow" @submit.prevent="resetSelectedAdminPassword">
              <label class="field">
                <span>重置密码</span>
                <div class="inputWithIcon">
                  <KeyRound :size="16" />
                  <input
                    v-model="adminEditForm.newPassword"
                    type="password"
                    required
                    minlength="10"
                    maxlength="128"
                    autocomplete="new-password"
                    placeholder="至少 10 位，包含字母和数字"
                  />
                </div>
              </label>
              <button class="secondaryButton" type="submit" :disabled="saving">
                重置并注销原会话
              </button>
            </form>
              </section>
            </div>
          </section>
        </template>

        <template v-else-if="activeView === 'account'">
          <section class="sectionIntro">
            <div>
              <span class="eyebrow">当前登录身份</span>
              <h2>账号安全</h2>
              <p>修改密码后，当前会话会自动续签，其他设备上的管理会话全部失效。</p>
            </div>
          </section>
          <section class="accountSecurityGrid">
            <article class="panel identityCard">
              <div class="adminAvatar">{{ adminInitials(currentAdmin) }}</div>
              <div>
                <span class="eyebrow">{{ adminRoleLabel(currentAdmin) }}</span>
                <h3>{{ currentAdmin?.displayName }}</h3>
                <p>@{{ currentAdmin?.username }}</p>
              </div>
              <div class="identityFacts">
                <span>账号状态<strong>{{ currentAdmin?.status === 'active' ? '正常' : '已停用' }}</strong></span>
                <span>权限范围<strong>{{ currentAdmin ? permissionSummary(currentAdmin) : '—' }}</strong></span>
                <span>最近登录<strong>{{ dateTime(currentAdmin?.lastLoginAt || undefined) }}</strong></span>
              </div>
            </article>
            <article class="panel passwordCard">
              <div class="panelHeader">
                <div><span class="eyebrow">凭据安全</span><h3>修改登录密码</h3></div>
                <LockKeyhole :size="21" />
              </div>
              <form class="stackedForm" @submit.prevent="changePassword">
                <label class="field">
                  <span>当前密码</span>
                  <input
                    v-model="passwordForm.currentPassword"
                    type="password"
                    required
                    maxlength="128"
                    autocomplete="current-password"
                  />
                </label>
                <label class="field">
                  <span>新密码</span>
                  <input
                    v-model="passwordForm.newPassword"
                    type="password"
                    required
                    minlength="10"
                    maxlength="128"
                    autocomplete="new-password"
                    placeholder="至少 10 位，包含字母和数字"
                  />
                </label>
                <label class="field">
                  <span>确认新密码</span>
                  <input
                    v-model="passwordForm.confirmPassword"
                    type="password"
                    required
                    minlength="10"
                    maxlength="128"
                    autocomplete="new-password"
                  />
                </label>
                <button class="primaryButton full" type="submit" :disabled="saving">
                  保存新密码
                </button>
              </form>
            </article>
          </section>
        </template>
      </div>
    </main>

    <UserLicensePanel v-if="licenseUser && !loginRequired" :key="licenseUser.id" :user="licenseUser" :access="userAccess" :loaded="accessLoaded"
      :loading="licenseLoading" :saving="licenseSaving" :can-read="can('licenses.read')" :can-write="can('licenses.write')"
      :error="licenseError" :notice="licenseNotice" @close="closeUserLicenses" @refresh="refreshUserLicenses"
      @save="mutateUserLicense" />

    <div v-if="loginRequired" class="loginOverlay">
      <form class="loginCard" @submit.prevent="submitLogin">
        <button
          class="loginThemeToggle"
          type="button"
          :aria-label="adminTheme === 'dark' ? '切换到白天主题' : '切换到深色主题'"
          :title="adminTheme === 'dark' ? '切换到白天主题' : '切换到深色主题'"
          @click="toggleAdminTheme"
        >
          <Sun v-if="adminTheme === 'dark'" :size="17" />
          <Moon v-else :size="17" />
          <span>{{ adminTheme === 'dark' ? '白天主题' : '深色主题' }}</span>
        </button>
        <div class="loginMark"><ShieldCheck :size="27" /></div>
        <span class="eyebrow">受保护的管理入口</span>
        <h2>管理员登录</h2>
        <p>使用管理员账号和密码进入授权范围内的后台模块。</p>
        <div v-if="errorMessage" class="alert error">{{ errorMessage }}</div>
        <label class="field">
          <span>管理员账号</span>
          <div class="inputWithIcon">
            <UserCog :size="16" />
            <input
              v-model="loginForm.username"
              autofocus
              required
              maxlength="32"
              autocomplete="username"
            />
          </div>
        </label>
        <label class="field">
          <span>密码</span>
          <div class="inputWithIcon">
            <KeyRound :size="16" />
            <input
              v-model="loginForm.password"
              type="password"
              required
              maxlength="128"
              autocomplete="current-password"
            />
          </div>
        </label>
        <button class="primaryButton full" type="submit" :disabled="saving">
          {{ saving ? '正在登录…' : '进入管理中心' }}
        </button>
      </form>
    </div>
  </div>
</template>
